import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AvatarUpload } from "../components/AvatarUpload.tsx";
import { Card, FIELD } from "../components/account-ui.tsx";
import { Button, ErrorMessage, HighlightedButton, Modal } from "../components/legacy/ui.tsx";
import { CheckIcon, PencilSquareIcon, ShieldCheckIcon, XMarkIcon } from "../icons/index.tsx";
import { api } from "../lib/api.ts";
import { authClient } from "../lib/auth-client.ts";
import { splitName } from "../lib/legacy.ts";
import { useProfile } from "./account.tsx";

export const Route = createFileRoute("/account/profile")({
  component: AccountProfile,
});

function AccountProfile() {
  const queryClient = useQueryClient();
  const { data } = useProfile();
  const profile = data?.profile;

  const [isEditing, setEditing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [form, setForm] = useState({
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    if (!profile) return;
    const { firstName, lastName } = splitName(profile.name);
    setForm({
      title: profile.organisation ?? "",
      firstName,
      lastName,
      email: profile.email,
      phone: profile.phone ?? "",
    });
  }, [profile]);

  const setLocation = useMutation({
    mutationFn: (useMyLocation: boolean) => api.patch("/account/profile", { useMyLocation }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["profile"] });
      await queryClient.invalidateQueries({ queryKey: ["addresses"] });
      await queryClient.invalidateQueries({ queryKey: ["config"] });
      await queryClient.invalidateQueries({ queryKey: ["customer"] });
    },
  });

  const save = useMutation({
    mutationFn: () =>
      api.patch("/account/profile", {
        name: `${form.firstName} ${form.lastName}`.trim(),
        organisation: form.title || null,
        phone: form.phone || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["profile"] });
      await queryClient.invalidateQueries({ queryKey: ["config"] });
      await queryClient.invalidateQueries({ queryKey: ["customer"] });
      if (profile && form.email !== profile.email) setShowModal(true);
      else setEditing(false);
    },
  });

  const updateEmail = async () => {
    const result = await authClient.changeEmail({
      newEmail: form.email,
      callbackURL: "/verify-email-address-change",
    });
    if (result.error) {
      setErrorMessage(result.error.message ?? "Could not change email");
      return;
    }
    setErrorMessage("");
    setEditing(false);
    setShowModal(false);
    await queryClient.invalidateQueries({ queryKey: ["profile"] });
  };

  if (!profile) return <div className="h-[100vh]" />;
  const { firstName, lastName } = splitName(profile.name);

  return (
    <div>
      <Card
        title="Profile"
        action={
          !isEditing && (
            <button
              type="button"
              className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50"
              onClick={() => setEditing(true)}
            >
              <PencilSquareIcon className="h-4 w-4" />
              Edit
            </button>
          )
        }
      >
        {isEditing ? (
          <div className="max-w-2xl">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <span className="mb-1 block text-sm font-medium text-gray-700">Organisation</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className={FIELD}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <span className="mb-1 block text-sm font-medium text-gray-700">First name</span>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    className={FIELD}
                  />
                </div>
                <div>
                  <span className="mb-1 block text-sm font-medium text-gray-700">Last name</span>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    className={FIELD}
                  />
                </div>
              </div>
              <div>
                <span className="mb-1 block text-sm font-medium text-gray-700">E-Mail</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={FIELD}
                />
              </div>
              <div>
                <span className="mb-1 block text-sm font-medium text-gray-700">Phone Number</span>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={FIELD}
                />
              </div>
            </div>
            <div className="mt-6 flex gap-x-4">
              <HighlightedButton onClick={() => save.mutate()} disabled={save.isPending}>
                <CheckIcon /> &nbsp; Save
              </HighlightedButton>
              <Button onClick={() => setEditing(false)}>
                <XMarkIcon forcedClass="w-4 h-4" /> &nbsp; Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
            <AvatarUpload
              name={profile.name}
              image={profile.image}
              updatedAt={profile.updatedAt}
              role={profile.role}
            />
            <dl className="grid flex-1 gap-4 sm:grid-cols-2">
              <Detail label="Name" value={`${firstName} ${lastName}`.trim()} />
              <Detail label="Organisation" value={profile.organisation} />
              <Detail label="E-Mail" value={profile.email} />
              <Detail label="Phone" value={profile.phone} />
            </dl>
          </div>
        )}
        <LocationSwitch
          checked={profile.use_my_location === 1}
          disabled={setLocation.isPending}
          onChange={() => setLocation.mutate(profile.use_my_location !== 1)}
        />
      </Card>
      <Modal
        open={showModal}
        title="Confirm E-Mail address change"
        onSubmit={() => updateEmail()}
        onCancel={() => setShowModal(false)}
        icon={<ShieldCheckIcon />}
      >
        <div className="space-y-4">
          <p>
            We will send a confirmation to your current address. Enter your current password to
            confirm.
          </p>
          <input
            type="password"
            placeholder="Current password"
            className={FIELD}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          {errorMessage !== "" && (
            <ErrorMessage
              heading="We ran into a problem changing your email!"
              message={errorMessage}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}

function LocationSwitch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
}) {
  return (
    <div className="mt-6 border-t border-gray-100 pt-5">
      <label
        className={`tooltip tooltip-top flex w-fit items-center gap-3 text-left before:max-w-xs before:whitespace-normal ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
        data-tip="The map opens where you are. Turning this on turns off Use Address Location. Off, the map uses the address you marked, or your default location."
      >
        <input
          type="checkbox"
          role="switch"
          aria-checked={checked}
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
        />
        <span
          aria-hidden
          className="pointer-events-none relative h-5 w-9 shrink-0 rounded-full bg-gray-300 transition-colors peer-checked:bg-primary-600 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary-600 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:after:translate-x-4"
        />
        <span>
          <span className="block text-sm font-medium text-gray-900">Use My Location</span>
          <span className="mt-0.5 block text-sm text-gray-500">
            {checked ? "The map opens where you are." : "The map opens on your saved address."}
          </span>
        </span>
      </label>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-gray-900">{value || "Not set"}</dd>
    </div>
  );
}
